// AllContacts.jsx
// Route: /allcontacts
// Add to router: <Route path="/allcontacts" element={<AllContacts />} />
//
// REQUIRED BACKEND: GET /api/contacts/all
// See comment at bottom of file for the Spring Boot snippet.

import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

// ── Confirmation Modal ──────────────────────────────────────────────────────
const ConfirmModal = ({ show, title, message, confirmLabel, confirmVariant = "danger", onConfirm, onCancel }) => {
  if (!show) return null;
  return (
    <>
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050 }} onClick={onCancel} />
      <div role="dialog" aria-modal="true" style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 1055, width: "100%", maxWidth: 420, background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.18)", padding: "28px 28px 22px" }}>
        <div className="d-flex align-items-center gap-3 mb-3">
          <div style={{ width: 44, height: 44, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#fff0f0" }}>
            <i className="bi bi-trash3" style={{ fontSize: 20, color: "#dc3545" }} />
          </div>
          <h6 className="mb-0 fw-bold" style={{ fontSize: 16 }}>{title}</h6>
        </div>
        <p className="text-muted mb-4" style={{ fontSize: 14, lineHeight: 1.6 }}>{message}</p>
        <div className="d-flex justify-content-end gap-2">
          <button className={`btn btn-sm btn-${confirmVariant} px-4`} style={{ borderRadius: 8 }} onClick={onConfirm}>{confirmLabel}</button>
          <button className="btn btn-sm btn-outline-secondary px-4" style={{ borderRadius: 8 }} onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </>
  );
};

// ───────────────────────────────────────────────────────────────────────────
const AllContacts = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [deptFilter, setDeptFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [primaryFilter, setPrimaryFilter] = useState("");

  // Export
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Modal
  const [modal, setModal] = useState({ show: false, contact: null });
  const openDeleteModal = (contact) => setModal({ show: true, contact });
  const closeModal = () => setModal({ show: false, contact: null });

  // Bootstrap loader
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const il = document.createElement("link"); il.rel = "stylesheet"; il.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(il);
    return () => { bl.remove(); il.remove(); };
  }, []);

  // Auth check
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_CUSTOMER_MANAGER")) { toast.error("Unauthorized."); navigate("/"); return; }
  }, [navigate, token]);

  // Fetch ALL contacts across all customers
  const fetchContacts = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}/api/contacts/all`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch contacts"); return res.json(); })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.content || [];
        setContacts(list);
        setFilteredContacts(list);
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  };

  useEffect(() => { fetchContacts(); }, [token]);

  // Unique filter options derived from data
  const departments = [...new Set(contacts.map((c) => c.department).filter(Boolean))].sort();
  const customers   = [...new Set(contacts.map((c) => c.customerName).filter(Boolean))].sort();

  // Apply filters + sort
  useEffect(() => {
    let result = [...contacts];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter((c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.designation?.toLowerCase().includes(q) ||
        c.department?.toLowerCase().includes(q) ||
        c.customerName?.toLowerCase().includes(q)
      );
    }
    if (deptFilter)     result = result.filter((c) => c.department === deptFilter);
    if (customerFilter) result = result.filter((c) => c.customerName === customerFilter);
    if (primaryFilter === "yes") result = result.filter((c) => c.primary);
    if (primaryFilter === "no")  result = result.filter((c) => !c.primary);

    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = a[sortConfig.key] ?? "";
        const valB = b[sortConfig.key] ?? "";
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredContacts(result);
  }, [search, deptFilter, customerFilter, primaryFilter, contacts, sortConfig]);

  const handleSort = (key) => setSortConfig((prev) => ({ key, direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc" }));
  const getSortArrow = (key) => sortConfig.key !== key ? "" : sortConfig.direction === "asc" ? "↑" : "↓";

  // Delete — still uses /api/customers/{customerId}/contacts/{id} since that's your existing endpoint
  const handleConfirmDelete = async () => {
    const contact = modal.contact;
    closeModal();
    try {
      const res = await fetch(`${API_BASE_URL}/api/customers/${contact.customerId}/contacts/${contact.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete contact.");
      toast.success("Contact deleted.");
      setContacts((prev) => prev.filter((c) => c.id !== contact.id));
    } catch (err) { toast.error(err.message || "Failed to delete contact."); }
  };

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const res = await fetch(`${API_BASE_URL}/api/reports/contacts?format=excel`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Contact_Report.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
      setShowExportMenu(false);
    } catch { toast.error("Failed to download report."); } finally { setDownloading(false); }
  };

  const initials = (name = "") => name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "??";

  const primaryCount = filteredContacts.filter((c) => c.primary).length;

  return (
    <>
      <ConfirmModal
        show={modal.show}
        title="Delete Contact"
        message={`Are you sure you want to permanently delete "${modal.contact?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={closeModal}
      />

      <div className="fixed-top"><AdminNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "60px" }}></div>

      <div className="d-flex flex-column min-vh-100">
        <div className="container-fluid p-3 bg-light flex-grow-1">

          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold mb-0">
              <i className="bi bi-person-lines-fill me-2"></i> Contacts
            </h4>
            <div className="d-flex gap-2 align-items-center">
              <div style={{ position: "relative" }}>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowExportMenu((p) => !p)} disabled={downloading}>
                  {downloading ? "Exporting…" : "↓ Export ▾"}
                </button>
                {showExportMenu && (
                  <div style={{ position: "absolute", top: "110%", right: 0, background: "#fff", border: "1px solid #e2e2e2", borderRadius: 8, boxShadow: "0 8px 20px rgba(0,0,0,0.08)", minWidth: 190, zIndex: 1000 }} onMouseLeave={() => setShowExportMenu(false)}>
                    <div className="px-3 py-2" style={{ cursor: "pointer", fontSize: 13 }} onClick={downloadReport}>
                      📊 Export Excel
                    </div>
                  </div>
                )}
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => navigate("/addcontact")}>
                + New Contact
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="d-flex align-items-center gap-3 mb-3 p-2 bg-white border rounded">
            <small className="fw-semibold text-muted text-uppercase" style={{ letterSpacing: "0.5px" }}>Overview</small>
            <span className="badge rounded-pill bg-light text-dark border">{filteredContacts.length} Total</span>
            <span className="badge rounded-pill" style={{ background: "#edf5ed", color: "#356035", border: "1px solid #c8e6c9" }}>★ {primaryCount} Primary</span>
            <span className="badge rounded-pill" style={{ background: "#f5f5f3", color: "#888", border: "1px solid #e2e2e2" }}>{filteredContacts.length - primaryCount} Non-primary</span>
          </div>

          {/* Collapsible Filters */}
          <div className="p-2 bg-light border mb-2" style={{ cursor: "pointer" }} onClick={() => setShowFilters(!showFilters)}>
            <strong>{showFilters ? "− Filters" : "+ Filters"}</strong>
          </div>
          {showFilters && (
            <div className="row mb-3">
              <div className="col-md-4 mb-2">
                <input type="text" className="form-control" placeholder="Search name, email, role, customer…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={customerFilter} onChange={(e) => setCustomerFilter(e.target.value)}>
                  <option value="">All Customers</option>
                  {customers.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
                  <option value="">All Departments</option>
                  {departments.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-select" value={primaryFilter} onChange={(e) => setPrimaryFilter(e.target.value)}>
                  <option value="">All Types</option>
                  <option value="yes">Primary Only</option>
                  <option value="no">Non-Primary Only</option>
                </select>
              </div>
            </div>
          )}

          {/* Toolbar */}
          <div className="d-flex justify-content-between align-items-center p-2 bg-light border mb-2">
            <span className="text-muted small">Total: {filteredContacts.length}</span>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-5 text-muted">
              <div className="spinner-border spinner-border-sm me-2" /> Loading contacts…
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm align-middle text-start">
                <thead>
                  <tr>
                    <th>View | Edit</th>
                    <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>Name {getSortArrow("name")}</th>
                    <th>Primary</th>
                    <th onClick={() => handleSort("designation")} style={{ cursor: "pointer" }}>Designation {getSortArrow("designation")}</th>
                    <th onClick={() => handleSort("department")} style={{ cursor: "pointer" }}>Department {getSortArrow("department")}</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th onClick={() => handleSort("customerName")} style={{ cursor: "pointer" }}>Customer {getSortArrow("customerName")}</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredContacts.length > 0 ? filteredContacts.map((c) => (
                    <tr key={c.id} style={{ background: c.primary ? "#f0f5ff" : "" }}>
                      <td>
                        <Link to={`/viewcontact/${c.id}`} className="me-1 text-primary">View</Link>
                        |
                        <Link to={`/editcontact/${c.id}`} className="ms-1 text-primary">Edit</Link>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: c.primary ? "#dbeafe" : "#eaf0f8", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 600, color: c.primary ? "#1d4ed8" : "#3a6090" }}>
                            {initials(c.name)}
                          </div>
                          <span className="fw-normal">{c.name}</span>
                        </div>
                      </td>
                      <td>
                        {c.primary
                          ? <span className="badge" style={{ background: "#1d4ed8", color: "#fff", fontSize: 10 }}>★ Primary</span>
                          : <span className="text-muted" style={{ fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ fontSize: 12.5 }}>{c.designation || "—"}</td>
                      <td style={{ fontSize: 12.5 }}>{c.department || "—"}</td>
                      <td style={{ fontSize: 12.5 }}>
                        {c.email ? <a href={`mailto:${c.email}`} style={{ color: "#0052CC", textDecoration: "none" }}>{c.email}</a> : "—"}
                      </td>
                      <td style={{ fontSize: 12.5 }}>{c.phone || "—"}</td>
                      <td>
                        {c.customerId
                          ? <Link to={`/viewcustomer/${c.customerId}`} style={{ color: "#0052CC", textDecoration: "none", fontSize: 12.5 }}>{c.customerName || "—"}</Link>
                          : <span style={{ fontSize: 12.5 }}>{c.customerName || "—"}</span>}
                      </td>
                      <td>
                        <button className="btn btn-sm btn-outline-danger" title="Delete" onClick={() => openDeleteModal(c)}>
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-muted text-center py-4">
                        {contacts.length === 0 ? "No contacts found." : "No contacts match the current filters."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {error && <p className="text-danger mt-3">{error}</p>}
        </div>
      </div>
    </>
  );
};

export default AllContacts;

